"use client";

import { LOCALE_AUTONYMS } from "@marketing-sites/l10n/preference/locale-names";
import { cn } from "@marketing-sites/utils/cn";

export interface LocaleSwitcherLinksProps {
  prefixes: string[];
  currentPrefix: string;
  resolveHref: (prefix: string) => string;
  /**
   * The link's `lang`. A URL prefix is not a language tag — `es-la` parses as
   * Spanish-as-spoken-in-Laos — so the app resolves each prefix to its locale
   * identifier (`localeConfig.resolveLocaleFromPrefix`) instead.
   */
  resolveLang: (prefix: string) => string | null;
  /** Persists the choice. Supplied by the app, which owns the LocaleConfig the cookie helper needs. */
  onSelect?: (prefix: string) => void;
  className?: string;
  linkClassName?: string;
}

export function LocaleSwitcherLinks({
  prefixes,
  currentPrefix,
  resolveHref,
  resolveLang,
  onSelect,
  className,
  linkClassName,
}: LocaleSwitcherLinksProps) {
  return (
    <ul className={className}>
      {prefixes.map((prefix) => (
        <li key={prefix}>
          {/* Plain <a>, not next/link: a locale switch must be a full document request so the server re-renders under the new locale. */}
          <a
            href={resolveHref(prefix)}
            className={linkClassName}
            lang={resolveLang(prefix) ?? undefined}
            aria-current={prefix === currentPrefix ? "true" : undefined}
            onClick={() => onSelect?.(prefix)}
          >
            {LOCALE_AUTONYMS[prefix] ?? prefix}
          </a>
        </li>
      ))}
    </ul>
  );
}

export interface LocaleSwitcherProps
  extends Omit<LocaleSwitcherLinksProps, "className"> {
  heading: string;
  className?: string;
  headingClassName?: string;
  listClassName?: string;
}

export function LocaleSwitcher({
  prefixes,
  currentPrefix,
  resolveHref,
  resolveLang,
  onSelect,
  heading,
  className,
  headingClassName,
  listClassName,
  linkClassName,
}: LocaleSwitcherProps) {
  return (
    <nav aria-label={heading} className={cn(className)}>
      <span data-slot="label" className={headingClassName}>
        {heading}
      </span>
      <LocaleSwitcherLinks
        prefixes={prefixes}
        currentPrefix={currentPrefix}
        resolveHref={resolveHref}
        resolveLang={resolveLang}
        onSelect={onSelect}
        className={listClassName}
        linkClassName={linkClassName}
      />
    </nav>
  );
}
